import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import ReceiptWizard from './ReceiptWizard';
import { ReceiptReportsView } from './ReceiptReportsView';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Receipt,
  Search,
  Plus,
  Printer,
  Check,
  Clock,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Building2,
  X,
  ClipboardList,
  PlusCircle,
  MinusCircle,
  Package,
  Users,
  Edit3,
  ChevronsUpDown,
  AlertCircle,
  BarChart3,
  Eye,
  Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Searchable Combobox Component
const SearchableSelect = ({ options, value, onChange, placeholder, icon: Icon, disabled }) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const selectedOption = options.find(opt => opt.id === value);
  
  const filteredOptions = options.filter(opt => 
    opt.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    opt.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    opt.project_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          className="flex items-center justify-between w-full px-3 py-2 text-sm border rounded-lg bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center gap-2 truncate">
            {Icon && <Icon className="w-4 h-4 text-gray-400 shrink-0" />}
            <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
              {selectedOption?.name || selectedOption?.label || (selectedOption?.project_number ? `${selectedOption.project_number} - ${selectedOption.project_name}` : placeholder)}
            </span>
          </div>
          <ChevronsUpDown className="w-4 h-4 text-gray-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={`ابحث...`} 
            value={searchQuery}
            onValueChange={setSearchQuery}
            className="text-right"
          />
          <CommandList>
            <CommandEmpty>لا توجد نتائج</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => {
                    onChange(option.id);
                    setOpen(false);
                    setSearchQuery('');
                  }}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <span>{option.name || option.label || `${option.project_number} - ${option.project_name}`}</span>
                  {value === option.id && (
                    <Check className="w-4 h-4 text-blue-600" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const ReceiptManagement = () => {
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id;
  const [deliveries, setDeliveries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [activeUsers, setActiveUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [userRole, setUserRole] = useState('receipt_entry');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingDelivery, setRejectingDelivery] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [activeTab, setActiveTab] = useState('list'); // list, pending, reports
  
  // ===== ميزة قائمة مراجعة المعتمِد (Approver Checklist) =====
  // مفاتيح الاستلامات المحددة بصيغة "supplierIndex:receiptIndex"
  const [checkedReceiptKeys, setCheckedReceiptKeys] = useState(new Set());
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);

  const emptyReceipt = { receipt_number: '', receipt_date: '', project_id: '', project_number: '', project_name: '', notes: '' };
  const emptySupplier = { supplier_id: '', supplier_name: '', receipts: [{ ...emptyReceipt }] };

  const [formData, setFormData] = useState({
    suppliers: [{ ...emptySupplier }],
    notes: ''
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchDeliveries();
  }, [page, statusFilter, userFilter]);

  // إعادة جلب البيانات عند تغيير البحث (debounced) — حتى يبحث على الـ backend وليس الصفحة فقط
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchDeliveries();
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // فتح كشف في النافذة + تهيئة قائمة المراجعة للمعتمِد (كل الاستلامات غير محددة افتراضياً — المعتمِد يحدّد ما راجعه)
  const openDelivery = (delivery) => {
    setSelectedDelivery(delivery);
    // البدء بمجموعة فارغة دائماً — المستخدم مُلزَم بمراجعة كل استلام وتحديده يدوياً
    setCheckedReceiptKeys(new Set());
  };

  const closeDelivery = () => {
    setSelectedDelivery(null);
    setCheckedReceiptKeys(new Set());
  };

  const fetchData = async () => {
    try {
      const suppliersRes = await fetch(`${API_URL}/api/procurement/suppliers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (suppliersRes.ok) {
        const suppliersData = await suppliersRes.json();
        setSuppliers(suppliersData || []);
      }
      
      const projectsRes = await fetch(`${API_URL}/api/procurement/my-projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        setProjects(projectsData || []);
      }
      
      fetchStats();
      fetchActiveUsers();
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchActiveUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/receipts/users/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActiveUsers(data.users || []);
      }
    } catch (error) {
      // silent
    }
  };

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      if (statusFilter) params.append('status', statusFilter);
      if (userFilter) params.append('user_id', userFilter);
      if (searchQuery && searchQuery.trim()) params.append('search', searchQuery.trim());
      
      const response = await fetch(`${API_URL}/api/receipts/deliveries?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDeliveries(data.deliveries || []);
        setTotalPages(data.total_pages || 1);
        setUserRole(data.user_role || 'receipt_entry');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/receipts/stats/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
        setUserRole(data.user_role || 'receipt_entry');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // إدارة الموردين
  const addSupplier = () => {
    setFormData(prev => ({
      ...prev,
      suppliers: [...prev.suppliers, { ...emptySupplier }]
    }));
  };

  const removeSupplier = (index) => {
    if (formData.suppliers.length > 1) {
      setFormData(prev => ({
        ...prev,
        suppliers: prev.suppliers.filter((_, i) => i !== index)
      }));
    }
  };

  const updateSupplier = (index, field, value) => {
    const newSuppliers = [...formData.suppliers];
    newSuppliers[index][field] = value;
    
    if (field === 'supplier_id') {
      const supplier = suppliers.find(s => s.id === value);
      newSuppliers[index].supplier_name = supplier?.name || '';
    }
    
    setFormData(prev => ({ ...prev, suppliers: newSuppliers }));
  };

  // إدارة الاستلامات
  const addReceipt = (supplierIndex) => {
    const newSuppliers = [...formData.suppliers];
    newSuppliers[supplierIndex].receipts.push({ ...emptyReceipt });
    setFormData(prev => ({ ...prev, suppliers: newSuppliers }));
  };

  const removeReceipt = (supplierIndex, receiptIndex) => {
    const newSuppliers = [...formData.suppliers];
    if (newSuppliers[supplierIndex].receipts.length > 1) {
      newSuppliers[supplierIndex].receipts = newSuppliers[supplierIndex].receipts.filter((_, i) => i !== receiptIndex);
      setFormData(prev => ({ ...prev, suppliers: newSuppliers }));
    }
  };

  const updateReceipt = (supplierIndex, receiptIndex, field, value) => {
    const newSuppliers = [...formData.suppliers];
    newSuppliers[supplierIndex].receipts[receiptIndex][field] = value;
    
    if (field === 'project_id') {
      const project = projects.find(p => p.id === value);
      newSuppliers[supplierIndex].receipts[receiptIndex].project_number = project?.project_number || '';
      newSuppliers[supplierIndex].receipts[receiptIndex].project_name = project?.project_name || '';
    }
    
    setFormData(prev => ({ ...prev, suppliers: newSuppliers }));
  };

  // حفظ الكشف
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // التحقق من البيانات
    let hasError = false;
    for (const supplier of formData.suppliers) {
      if (!supplier.supplier_id) {
        toast.error('يرجى اختيار جميع الموردين');
        hasError = true;
        break;
      }
      for (const receipt of supplier.receipts) {
        if (!receipt.receipt_number.trim()) {
          toast.error('يرجى إدخال رقم الاستلام لجميع الاستلامات');
          hasError = true;
          break;
        }
      }
      if (hasError) break;
    }
    if (hasError) return;
    
    try {
      setLoading(true);
      
      const url = editMode 
        ? `${API_URL}/api/receipts/delivery/${editingDelivery.id}`
        : `${API_URL}/api/receipts/delivery`;
      
      const response = await fetch(url, {
        method: editMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success(editMode ? 'تم تعديل كشف الاستلام بنجاح' : 'تم إنشاء كشف الاستلام بنجاح');
        resetForm();
        fetchDeliveries();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'حدث خطأ');
      }
    } catch (error) {
      toast.error('فشل في حفظ كشف الاستلام');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      suppliers: [{ ...emptySupplier }],
      notes: ''
    });
    setShowForm(false);
    setEditMode(false);
    setEditingDelivery(null);
  };

  // تعديل كشف
  const handleEdit = (delivery) => {
    setEditMode(true);
    setEditingDelivery(delivery);
    setFormData({
      suppliers: delivery.suppliers.map(s => ({
        supplier_id: s.supplier_id,
        supplier_name: s.supplier_name,
        receipts: s.receipts.map(r => ({
          receipt_number: r.receipt_number,
          receipt_date: r.receipt_date || '',
          project_id: r.project_id || '',
          project_number: r.project_number || '',
          project_name: r.project_name || '',
          notes: r.notes || ''
        }))
      })),
      notes: delivery.notes || ''
    });
    setShowForm(true);
  };

  // حذف كشف
  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الكشف؟')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/receipts/delivery/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('تم حذف الكشف بنجاح');
        fetchDeliveries();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'فشل في الحذف');
      }
    } catch (error) {
      toast.error('فشل في حذف الكشف');
    }
  };

  // اعتماد كشف (يدعم الاعتماد الجزئي عبر approvedKeys)
  const handleApprove = async (id, approvedKeys = null) => {
    try {
      const body = { action: 'approve' };
      if (approvedKeys !== null) {
        body.approved_receipt_keys = Array.from(approvedKeys);
      }
      const response = await fetch(`${API_URL}/api/receipts/delivery/${id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'تم اعتماد الكشف بنجاح');
        fetchDeliveries();
        fetchStats();
        // إغلاق نوافذ القوائم
        setShowApprovalConfirm(false);
        setSelectedDelivery(null);
        setCheckedReceiptKeys(new Set());
      } else {
        const error = await response.json();
        toast.error(error.detail || 'فشل في الاعتماد');
      }
    } catch (error) {
      toast.error('فشل في اعتماد الكشف');
    }
  };

  // رفض كشف
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('سبب الرفض مطلوب');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/receipts/delivery/${rejectingDelivery.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'reject', rejection_reason: rejectionReason })
      });
      
      if (response.ok) {
        toast.success('تم رفض الكشف');
        setShowRejectModal(false);
        setRejectingDelivery(null);
        setRejectionReason('');
        fetchDeliveries();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'فشل في الرفض');
      }
    } catch (error) {
      toast.error('فشل في رفض الكشف');
    }
  };

  // تأكيد الجاهزية (اعتماد مبدئي) - draft → pending
  const handleSubmitForApproval = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/receipts/delivery/${id}/submit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('تم تأكيد جاهزية الكشف للاعتماد النهائي');
        fetchDeliveries();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'فشل في تأكيد الجاهزية');
      }
    } catch (error) {
      toast.error('فشل في تأكيد الجاهزية');
    }
  };

  // إرجاع إلى مسودة - pending → draft
  const handleUnsubmit = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/receipts/delivery/${id}/unsubmit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('تم إرجاع الكشف إلى مسودة');
        fetchDeliveries();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'فشل');
      }
    } catch (error) {
      toast.error('فشل');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700 border-gray-300',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300'
    };
    const labels = { draft: 'مسودة', pending: 'جاهز للاعتماد', approved: 'معتمد', rejected: 'مرفوض' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs border ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  // البحث الآن يتم على الـ backend (يشمل كل الصفحات)، فالقائمة الواردة جاهزة للعرض
  const filteredDeliveries = deliveries;

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg shrink-0">
              <Receipt className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">إدارة كشوف الاستلام</h1>
              <p className="text-gray-500 text-xs sm:text-sm">
                {userRole === 'receipt_admin' ? 'مدير النظام' : 
                 userRole === 'receipt_approver' ? 'معتمد' : 'مدخل بيانات'}
              </p>
            </div>
          </div>

          {/* Stats - 3 cols on mobile, flex-wrap on desktop */}
          <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-3">
            <div className="bg-white rounded-xl px-3 py-2 border shadow-sm text-center sm:text-right">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_deliveries || 0}</p>
              <p className="text-gray-500 text-[10px] sm:text-xs">كشوف</p>
            </div>
            <div className="bg-white rounded-xl px-3 py-2 border shadow-sm text-center sm:text-right">
              <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats.total_receipts || 0}</p>
              <p className="text-gray-500 text-[10px] sm:text-xs">استلامات</p>
            </div>
            <div className="bg-gray-50 rounded-xl px-3 py-2 border border-gray-200 text-center sm:text-right">
              <p className="text-xl sm:text-2xl font-bold text-gray-700">{stats.draft || 0}</p>
              <p className="text-gray-600 text-[10px] sm:text-xs">مسودة</p>
            </div>
            <div className="bg-yellow-50 rounded-xl px-3 py-2 border border-yellow-200 text-center sm:text-right">
              <p className="text-xl sm:text-2xl font-bold text-yellow-600">{stats.pending || 0}</p>
              <p className="text-yellow-600 text-[10px] sm:text-xs">جاهز</p>
            </div>
            <div className="bg-green-50 rounded-xl px-3 py-2 border border-green-200 text-center sm:text-right">
              <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.approved || 0}</p>
              <p className="text-green-600 text-[10px] sm:text-xs">معتمد</p>
            </div>
          </div>
        </div>

        {/* Action Bar — sticky on mobile */}
        <div className="sm:hidden mb-3">
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            data-testid="new-receipt-mobile-btn"
            className="w-full bg-gradient-to-r from-orange-500 to-amber-600 text-white px-4 py-3 rounded-xl text-base font-bold shadow-md active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" />
            كشف استلام جديد
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
          <button
            onClick={() => setActiveTab('list')}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1 ${
              activeTab === 'list' ? 'bg-orange-600 text-white shadow-md' : 'bg-white text-gray-600 border hover:bg-gray-50'
            }`}
          >
            <ClipboardList className="w-4 h-4" />
            <span className="hidden sm:inline">كشوف الاستلام</span>
            <span className="sm:hidden">الكشوف</span>
          </button>
          {userRole !== 'receipt_entry' && (
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                activeTab === 'pending' ? 'bg-orange-600 text-white shadow-md' : 'bg-white text-gray-600 border hover:bg-gray-50'
              }`}
            >
              <Clock className="w-4 h-4" />
              للاعتماد
              {stats.pending > 0 && (
                <span className="bg-yellow-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{stats.pending}</span>
              )}
            </button>
          )}
          {userRole !== 'receipt_entry' && (
            <button
              onClick={() => setActiveTab('reports')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                activeTab === 'reports' ? 'bg-orange-600 text-white shadow-md' : 'bg-white text-gray-600 border hover:bg-gray-50'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              التقارير
            </button>
          )}
          {/* Desktop-only "New receipt" button */}
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="hidden sm:flex sm:mr-auto bg-gradient-to-r from-orange-500 to-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-md hover:shadow-lg transition-all items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            كشف استلام جديد
          </button>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl border shadow-sm p-3 sm:p-6">
          
          {/* Wizard Modal (mobile-first responsive) */}
          {/* استخدام key لإجبار remount عند فتح الكشف الجديد بعد التعديل */}
          {showForm && (
            <ReceiptWizard
              key={editMode ? `edit-${editingDelivery?.id}` : 'new'}
              open={showForm}
              onClose={resetForm}
              onSuccess={() => { resetForm(); fetchDeliveries(); fetchStats(); }}
              suppliers={suppliers}
              projects={projects}
              token={token}
              editMode={editMode}
              editingDelivery={editingDelivery}
            />
          )}

          {/* List Tab */}
          {activeTab === 'list' && !showForm && (
            <div>
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="بحث بالرقم أو المورد..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full h-11 sm:h-10 border rounded-lg pr-10 pl-4 text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="h-11 sm:h-10 border rounded-lg px-3 text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none bg-white"
                >
                  <option value="">كل الحالات</option>
                  <option value="draft">مسودة</option>
                  <option value="pending">جاهز للاعتماد</option>
                  <option value="approved">معتمد</option>
                  <option value="rejected">مرفوض</option>
                </select>
                {/* User filter — للمعتمدين والمدراء فقط */}
                {userRole !== 'receipt_entry' && activeUsers.length > 0 && (
                  <select
                    value={userFilter}
                    onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
                    data-testid="user-filter-select"
                    className="h-11 sm:h-10 border rounded-lg px-3 text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none bg-white"
                  >
                    <option value="">كل المستخدمين</option>
                    {activeUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.name} ({u.count})</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Table */}
              {loading ? (
                <div className="text-center py-12 text-gray-500">جاري التحميل...</div>
              ) : filteredDeliveries.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>لا توجد كشوف استلام</p>
                </div>
              ) : (
                <>
                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {filteredDeliveries.map((delivery) => (
                    <MobileDeliveryCard
                      key={delivery.id}
                      delivery={delivery}
                      currentUserId={currentUserId}
                      userRole={userRole}
                      getStatusBadge={getStatusBadge}
                      onView={() => openDelivery(delivery)}
                      onEdit={() => handleEdit(delivery)}
                      onDelete={() => handleDelete(delivery.id)}
                      onSubmit={() => handleSubmitForApproval(delivery.id)}
                      onUnsubmit={() => handleUnsubmit(delivery.id)}
                      onApprove={() => handleApprove(delivery.id)}
                      onReject={() => { setRejectingDelivery(delivery); setShowRejectModal(true); }}
                      onPrint={() => { setSelectedDelivery(delivery); setShowPrintModal(true); }}
                    />
                  ))}
                </div>

                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-right py-3 px-4 text-gray-500 font-medium text-sm">رقم الكشف</th>
                        <th className="text-right py-3 px-4 text-gray-500 font-medium text-sm">التاريخ</th>
                        <th className="text-right py-3 px-4 text-gray-500 font-medium text-sm">الموردين</th>
                        <th className="text-right py-3 px-4 text-gray-500 font-medium text-sm">الاستلامات</th>
                        <th className="text-right py-3 px-4 text-gray-500 font-medium text-sm">الحالة</th>
                        <th className="text-right py-3 px-4 text-gray-500 font-medium text-sm">بواسطة</th>
                        <th className="text-right py-3 px-4 text-gray-500 font-medium text-sm">الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDeliveries.map((delivery) => (
                        <tr key={delivery.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-gray-900">#{delivery.delivery_number}</td>
                          <td className="py-3 px-4 text-gray-600">{delivery.delivery_date}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              <Users className="w-4 h-4 text-gray-400" />
                              <span>{delivery.suppliers_count}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              <Receipt className="w-4 h-4 text-gray-400" />
                              <span>{delivery.receipts_count}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">{getStatusBadge(delivery.status)}</td>
                          <td className="py-3 px-4 text-gray-500 text-sm">{delivery.created_by}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openDelivery(delivery)}
                                className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
                                title="عرض"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {(delivery.status === 'draft' || delivery.status === 'pending') && (
                                <>
                                  <button
                                    onClick={() => handleEdit(delivery)}
                                    className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-blue-600"
                                    title="تعديل"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(delivery.id)}
                                    className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-red-600"
                                    title="حذف"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                  {/* اعتماد مبدئي - draft → pending */}
                                  {delivery.status === 'draft' && (
                                    <button
                                      onClick={() => handleSubmitForApproval(delivery.id)}
                                      data-testid={`submit-receipt-btn-${delivery.id}`}
                                      className="p-1.5 hover:bg-blue-100 rounded text-gray-500 hover:text-blue-600"
                                      title="تأكيد الجاهزية (اعتماد مبدئي)"
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                  )}
                                  {/* إرجاع لمسودة */}
                                  {delivery.status === 'pending' && delivery.created_by_id === currentUserId && (
                                    <button
                                      onClick={() => handleUnsubmit(delivery.id)}
                                      data-testid={`unsubmit-receipt-btn-${delivery.id}`}
                                      className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
                                      title="إرجاع إلى مسودة"
                                    >
                                      <Clock className="w-4 h-4" />
                                    </button>
                                  )}
                                  {/* اعتماد نهائي ورفض - pending فقط ولغير المنشئ */}
                                  {delivery.status === 'pending' && userRole !== 'receipt_entry' && delivery.created_by_id !== currentUserId && (
                                    <>
                                      <button
                                        onClick={() => handleApprove(delivery.id)}
                                        data-testid={`approve-receipt-btn-${delivery.id}`}
                                        className="p-1.5 hover:bg-green-100 rounded text-gray-500 hover:text-green-600"
                                        title="اعتماد نهائي"
                                      >
                                        <Check className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => { setRejectingDelivery(delivery); setShowRejectModal(true); }}
                                        data-testid={`reject-receipt-btn-${delivery.id}`}
                                        className="p-1.5 hover:bg-red-100 rounded text-gray-500 hover:text-red-600"
                                        title="رفض"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </>
                                  )}
                                </>
                              )}
                              <button
                                onClick={() => { setSelectedDelivery(delivery); setShowPrintModal(true); }}
                                className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
                                title="طباعة"
                              >
                                <Printer className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 border rounded-lg disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600">صفحة {page} من {totalPages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 border rounded-lg disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Pending Tab */}
          {activeTab === 'pending' && userRole !== 'receipt_entry' && (
            <PendingDeliveriesTab 
              token={token}
              currentUserId={currentUserId}
              onApprove={handleApprove}
              onReject={(d) => { setRejectingDelivery(d); setShowRejectModal(true); }}
              onRefresh={() => { fetchDeliveries(); fetchStats(); }}
            />
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && userRole !== 'receipt_entry' && (
            <ReceiptReportsView token={token} />
          )}
        </div>
      </div>

      {/* View Modal */}
      {selectedDelivery && !showPrintModal && (() => {
        const canApproveHere = selectedDelivery.status === 'pending'
          && userRole !== 'receipt_entry'
          && selectedDelivery.created_by_id !== currentUserId;
        // إجمالي الاستلامات
        let totalReceipts = 0;
        (selectedDelivery.suppliers || []).forEach(sup => {
          totalReceipts += (sup.receipts || []).length;
        });
        const checkedCount = checkedReceiptKeys.size;
        const uncheckedCount = totalReceipts - checkedCount;

        const toggleKey = (key) => {
          setCheckedReceiptKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
          });
        };

        return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => closeDelivery()}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold">كشف استلام #{selectedDelivery.delivery_number}</h2>
              <button onClick={() => closeDelivery()} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-delivery-modal-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">التاريخ:</span>
                <span>{selectedDelivery.delivery_date}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">الحالة:</span>
                {getStatusBadge(selectedDelivery.status)}
              </div>
              {selectedDelivery.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-sm"><strong>سبب الرفض:</strong> {selectedDelivery.rejection_reason}</p>
                </div>
              )}

              {/* شريط تعليمات للمعتمِد */}
              {canApproveHere && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-start gap-2" data-testid="approver-instructions">
                  <ClipboardList className="w-5 h-5 mt-0.5 shrink-0 text-blue-600" />
                  <div>
                    <p className="font-medium mb-1">قائمة المراجعة</p>
                    <p className="text-blue-700">جميع الاستلامات <span className="font-bold">غير محددة</span> افتراضياً. اضغط <span className="font-bold">✓</span> بجانب كل استلام راجعته مع مدخل البيانات. أي استلام يبقى غير محدد سيُحذف تلقائياً عند الاعتماد.</p>
                  </div>
                </div>
              )}
              
              {selectedDelivery.suppliers?.map((supplier, idx) => (
                <div key={idx} className="border rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-orange-600" />
                    {supplier.supplier_name}
                  </h3>
                  <div className="space-y-2">
                    {supplier.receipts?.map((receipt, rIdx) => {
                      const key = `${idx}:${rIdx}`;
                      const isChecked = checkedReceiptKeys.has(key);
                      return (
                      <div
                        key={rIdx}
                        className={`rounded-lg p-3 text-sm flex items-start gap-3 transition-all ${
                          canApproveHere
                            ? (isChecked
                                ? 'bg-green-50 border border-green-300'
                                : 'bg-gray-50 border border-gray-200')
                            : 'bg-gray-50'
                        }`}
                      >
                        {canApproveHere && (
                          <div className="flex gap-2 items-center shrink-0" data-testid={`receipt-checklist-${idx}-${rIdx}`}>
                            <button
                              type="button"
                              onClick={() => !isChecked && toggleKey(key)}
                              data-testid={`receipt-check-yes-${idx}-${rIdx}`}
                              aria-label="موافق على هذا الاستلام"
                              className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition ${
                                isChecked
                                  ? 'bg-green-500 border-green-600 text-white shadow'
                                  : 'bg-white border-green-300 text-green-500 hover:bg-green-50'
                              }`}
                            >
                              <Check className="w-5 h-5" strokeWidth={3} />
                            </button>
                            <button
                              type="button"
                              onClick={() => isChecked && toggleKey(key)}
                              data-testid={`receipt-check-no-${idx}-${rIdx}`}
                              aria-label="حذف هذا الاستلام"
                              className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition ${
                                !isChecked
                                  ? 'bg-red-500 border-red-600 text-white shadow'
                                  : 'bg-white border-red-300 text-red-500 hover:bg-red-50'
                              }`}
                            >
                              <X className="w-5 h-5" strokeWidth={3} />
                            </button>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between mb-1">
                            <span className={`${canApproveHere && isChecked ? 'font-bold text-gray-900' : 'font-medium'}`}>{receipt.receipt_number}</span>
                            <span className="text-gray-500">{receipt.receipt_date}</span>
                          </div>
                          {receipt.project_number && (
                            <p className={`text-gray-600 ${canApproveHere && isChecked ? 'font-semibold' : ''}`}>المشروع: {receipt.project_number} - {receipt.project_name}</p>
                          )}
                          {receipt.notes && (
                            <p className="text-gray-500 mt-1">{receipt.notes}</p>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* شريط إجراءات المعتمِد */}
            {canApproveHere && (
              <div className="sticky bottom-0 bg-white border-t px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="approver-actions-bar">
                <div className="text-sm flex items-center justify-center sm:justify-start gap-4 flex-wrap">
                  <span className="text-green-700 font-medium flex items-center gap-1 whitespace-nowrap">
                    <Check className="w-4 h-4" /> محدد: <span data-testid="checked-count">{checkedCount}</span>
                  </span>
                  <span className="text-red-700 font-medium flex items-center gap-1 whitespace-nowrap">
                    <X className="w-4 h-4" /> غير محدد: <span data-testid="unchecked-count">{uncheckedCount}</span>
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setRejectingDelivery(selectedDelivery); setShowRejectModal(true); }}
                    data-testid="modal-reject-btn"
                    className="flex-1 sm:flex-initial bg-red-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 flex items-center justify-center gap-1 whitespace-nowrap"
                  >
                    <X className="w-4 h-4" /> رفض الكشف
                  </button>
                  <button
                    onClick={() => setShowApprovalConfirm(true)}
                    disabled={checkedCount === 0}
                    data-testid="modal-approve-btn"
                    className="flex-1 sm:flex-initial bg-green-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 whitespace-nowrap"
                  >
                    <Check className="w-4 h-4" /> اعتماد
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* Approval Confirmation Modal — تأكيد الاعتماد مع تحذير الحذف */}
      {showApprovalConfirm && selectedDelivery && (() => {
        let totalReceipts = 0;
        (selectedDelivery.suppliers || []).forEach(sup => {
          totalReceipts += (sup.receipts || []).length;
        });
        const checkedCount = checkedReceiptKeys.size;
        const uncheckedCount = totalReceipts - checkedCount;
        return (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={() => setShowApprovalConfirm(false)}>
            <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="approval-confirm-modal">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertCircle className={`w-5 h-5 ${uncheckedCount > 0 ? 'text-amber-500' : 'text-green-600'}`} />
                {uncheckedCount > 0 ? 'تأكيد الاعتماد الجزئي' : 'تأكيد الاعتماد'}
              </h3>

              <div className="space-y-3 mb-4">
                <p className="text-sm text-gray-700">
                  كشف رقم: <span className="font-semibold">#{selectedDelivery.delivery_number}</span>
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                  <span className="text-sm text-green-800 flex items-center gap-2"><Check className="w-4 h-4" /> سيتم اعتماد</span>
                  <span className="font-bold text-green-700">{checkedCount} استلام</span>
                </div>
                {uncheckedCount > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between" data-testid="warning-unchecked">
                    <span className="text-sm text-red-800 flex items-center gap-2"><Trash2 className="w-4 h-4" /> سيتم حذف</span>
                    <span className="font-bold text-red-700">{uncheckedCount} استلام</span>
                  </div>
                )}
                {uncheckedCount > 0 && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    تنبيه: حذف الاستلامات غير المحددة عملية نهائية ولا يمكن التراجع عنها.
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowApprovalConfirm(false)}
                  data-testid="approval-cancel-btn"
                  className="flex-1 border text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-50"
                >
                  إلغاء
                </button>
                <button
                  onClick={() => handleApprove(selectedDelivery.id, uncheckedCount > 0 ? checkedReceiptKeys : null)}
                  data-testid="approval-confirm-btn"
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg font-medium hover:bg-green-700"
                >
                  تأكيد الاعتماد
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Reject Modal */}
      {showRejectModal && rejectingDelivery && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowRejectModal(false); setRejectionReason(''); }}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              رفض كشف الاستلام
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              كشف رقم: <span className="font-medium">#{rejectingDelivery.delivery_number}</span>
            </p>
            <div>
              <label className="block text-sm text-gray-600 mb-1">سبب الرفض *</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows="3"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none resize-none"
                placeholder="أدخل سبب الرفض..."
                required
              />
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { setShowRejectModal(false); setRejectionReason(''); }}
                className="flex-1 border text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-50"
              >
                إلغاء
              </button>
              <button
                onClick={handleReject}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg font-medium hover:bg-red-700"
              >
                تأكيد الرفض
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Modal */}
      {showPrintModal && selectedDelivery && (
        <PrintModal 
          delivery={selectedDelivery} 
          onClose={() => { setShowPrintModal(false); setSelectedDelivery(null); }} 
        />
      )}
    </div>
  );
};

// Pending Deliveries Tab
const PendingDeliveriesTab = ({ token, currentUserId, onApprove, onReject, onRefresh }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    try {
      const response = await fetch(`${API_URL}/api/receipts/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeliveries(data.deliveries || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-500">جاري التحميل...</div>;

  if (deliveries.length === 0) {
    return (
      <div className="text-center py-12">
        <Check className="w-12 h-12 mx-auto mb-3 text-green-500" />
        <p className="text-gray-500">لا توجد كشوف معلقة للاعتماد</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {deliveries.map(delivery => {
        const isOwnDelivery = delivery.created_by_id === currentUserId;
        return (
        <div key={delivery.id} className="border rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-medium text-gray-900">كشف #{delivery.delivery_number}</p>
              <p className="text-gray-500 text-sm">{delivery.suppliers_count} موردين • {delivery.receipts_count} استلامات</p>
              <p className="text-gray-400 text-xs mt-1">بواسطة: {delivery.created_by} • {delivery.delivery_date}</p>
            </div>
            <div className="flex gap-2">
              {isOwnDelivery ? (
                <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  لا يمكنك اعتماد كشف أنشأته بنفسك
                </span>
              ) : (
                <>
                  <button
                    onClick={() => { onApprove(delivery.id); fetchPending(); onRefresh(); }}
                    data-testid={`pending-approve-btn-${delivery.id}`}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-1"
                  >
                    <Check className="w-4 h-4" />
                    اعتماد
                  </button>
                  <button
                    onClick={() => onReject(delivery)}
                    data-testid={`pending-reject-btn-${delivery.id}`}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    رفض
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
};


// Print Modal
const PrintModal = ({ delivery, onClose }) => {
  const handlePrint = () => {
    window.print();
  };

  const content = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 print:bg-white print:p-0 print:inset-auto print:static" data-print-root>
      {/* CSS لإخفاء كل ما عدا منطقة الطباعة عند الطباعة (display:none لإزالة الصفحات الفارغة) */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body { height: auto !important; overflow: visible !important; }
          body > *:not([data-print-root]) { display: none !important; }
          [data-print-root] { position: static !important; background: white !important; padding: 0 !important; display: block !important; height: auto !important; }
          [data-print-root] > div { box-shadow: none !important; max-width: 100% !important; max-height: none !important; overflow: visible !important; border-radius: 0 !important; }
          .print-hide-on-print { display: none !important; }
        }
      `}</style>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between print:hidden print-hide-on-print">
          <h2 className="text-lg font-bold">طباعة كشف الاستلام</h2>
          <div className="flex gap-2">
            <button onClick={handlePrint} className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
              <Printer className="w-4 h-4" />
              طباعة
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-8 print:p-4" dir="rtl">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold">كشف استلام</h1>
            <p className="text-gray-500">رقم: {delivery.delivery_number}</p>
          </div>
          
          <div className="flex justify-between mb-6 text-sm">
            <div>
              <p><strong>التاريخ:</strong> {delivery.delivery_date}</p>
              <p><strong>بواسطة:</strong> {delivery.created_by}</p>
            </div>
            <div>
              <p><strong>عدد الموردين:</strong> {delivery.suppliers_count}</p>
              <p><strong>عدد الاستلامات:</strong> {delivery.receipts_count}</p>
            </div>
          </div>
          
          {delivery.suppliers?.map((supplier, idx) => (
            <div key={idx} className="mb-6">
              <h3 className="font-bold text-lg border-b pb-2 mb-3">{supplier.supplier_name}</h3>
              <table className="w-full text-sm border">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-right">رقم الاستلام</th>
                    <th className="border p-2 text-right">التاريخ</th>
                    <th className="border p-2 text-right">المشروع</th>
                    <th className="border p-2 text-right">ملاحظات</th>
                  </tr>
                </thead>
                <tbody>
                  {supplier.receipts?.map((receipt, rIdx) => (
                    <tr key={rIdx}>
                      <td className="border p-2">{receipt.receipt_number}</td>
                      <td className="border p-2">{receipt.receipt_date || '-'}</td>
                      <td className="border p-2">{receipt.project_number ? `${receipt.project_number} - ${receipt.project_name}` : '-'}</td>
                      <td className="border p-2">{receipt.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          
          <div className="mt-12 flex justify-between text-sm">
            <div className="text-center">
              <p className="border-t pt-2 w-40">توقيع المُسلِّم</p>
            </div>
            <div className="text-center">
              <p className="border-t pt-2 w-40">توقيع المُستلِم</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render via Portal at document.body so print CSS can hide siblings cleanly
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
};

export default ReceiptManagement;

// ===================================================================
// MobileDeliveryCard — بطاقة الكشف في عرض الجوال
// ===================================================================
const MobileDeliveryCard = ({
  delivery, currentUserId, userRole, getStatusBadge,
  onView, onEdit, onDelete, onSubmit, onUnsubmit, onApprove, onReject, onPrint,
}) => {
  const isDraft = delivery.status === 'draft';
  const isPending = delivery.status === 'pending';
  const isMine = delivery.created_by_id === currentUserId;
  const editable = isDraft || isPending;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-3 shadow-sm" data-testid={`mobile-delivery-${delivery.id}`}>
      {/* Top row: number + status */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-bold text-lg text-gray-900">#{delivery.delivery_number}</span>
        </div>
        {getStatusBadge(delivery.status)}
      </div>

      {/* Info row */}
      <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
        <div className="flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5 text-gray-400" />
          <span>{delivery.delivery_date}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5 text-gray-400" />
            {delivery.suppliers_count}
          </span>
          <span className="flex items-center gap-1">
            <Receipt className="w-3.5 h-3.5 text-gray-400" />
            {delivery.receipts_count}
          </span>
        </div>
      </div>

      {/* Created by */}
      <div className="text-xs text-gray-500 mb-3 pb-3 border-b">
        بواسطة: {delivery.created_by}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={onView}
          className="flex-1 min-w-[44px] h-10 bg-gray-100 active:bg-gray-200 rounded-lg flex items-center justify-center gap-1 text-xs font-medium text-gray-700"
        >
          <Eye className="w-4 h-4" /> عرض
        </button>
        <button
          onClick={onPrint}
          className="flex-1 min-w-[44px] h-10 bg-gray-100 active:bg-gray-200 rounded-lg flex items-center justify-center gap-1 text-xs font-medium text-gray-700"
        >
          <Printer className="w-4 h-4" /> طباعة
        </button>
        {editable && (
          <>
            <button
              onClick={onEdit}
              data-testid={`mobile-edit-${delivery.id}`}
              className="flex-1 min-w-[44px] h-10 bg-blue-50 active:bg-blue-100 text-blue-700 rounded-lg flex items-center justify-center gap-1 text-xs font-medium"
            >
              <Edit3 className="w-4 h-4" /> تعديل
            </button>
            <button
              onClick={onDelete}
              data-testid={`mobile-delete-${delivery.id}`}
              className="h-10 px-3 bg-red-50 active:bg-red-100 text-red-700 rounded-lg flex items-center justify-center"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* Workflow actions */}
      {editable && (
        <div className="flex flex-wrap gap-2 mt-2">
          {isDraft && (
            <button
              onClick={onSubmit}
              data-testid={`mobile-submit-${delivery.id}`}
              className="flex-1 h-11 bg-blue-600 active:bg-blue-700 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-1"
            >
              <Check className="w-4 h-4" /> تأكيد الجاهزية
            </button>
          )}
          {isPending && isMine && (
            <button
              onClick={onUnsubmit}
              data-testid={`mobile-unsubmit-${delivery.id}`}
              className="flex-1 h-11 bg-gray-200 active:bg-gray-300 text-gray-800 rounded-lg font-semibold text-sm flex items-center justify-center gap-1"
            >
              <Clock className="w-4 h-4" /> إرجاع لمسودة
            </button>
          )}
          {isPending && userRole !== 'receipt_entry' && !isMine && (
            <>
              <button
                onClick={onApprove}
                data-testid={`mobile-approve-${delivery.id}`}
                className="flex-1 h-11 bg-green-600 active:bg-green-700 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-1"
              >
                <Check className="w-4 h-4" /> اعتماد
              </button>
              <button
                onClick={onReject}
                data-testid={`mobile-reject-${delivery.id}`}
                className="flex-1 h-11 bg-red-600 active:bg-red-700 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-1"
              >
                <X className="w-4 h-4" /> رفض
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
